// The buttons of a task: the words its sender chooses, what goes in the send, and what the
// preview draws on the new app and on an older one.

import { useState } from 'react';
import type { TaskType } from './bannerOptions';

/** The API's limit: a button holds a few words on a phone. */
export const MAX_BUTTON_WORDS = 40;

/** Mirrors the server's first line on a banner an older app cannot answer. Exactly this. */
export const UPDATE_TO_ANSWER_LINE = 'Please update your app to answer.';

/** The server's own sentence for this. */
const DETAIL_IN_WORDS = 'Button words cannot hold a detail such as a name.';

/** Which app the preview shows. */
export type PreviewApp = 'new' | 'older';

export function useTaskButtons({
  taskType,
  ctaLabel,
  formBannerOnly,
  formLine,
  released,
}: {
  /** null when the Task activity form is not open. */
  taskType: TaskType | null;
  /** The kind's fixed button: Open form, Upload resume. */
  ctaLabel: string | null;
  formBannerOnly: boolean;
  formLine: string | null;
  /** The app build with the buttons is in both stores. Until then Read this has no button. */
  released: boolean;
}) {
  const [agreeWords, setAgreeWords] = useState('I agree');
  const [declineWords, setDeclineWords] = useState('I decline');
  const [allowDecline, setAllowDecline] = useState(true);
  const [buttonWords, setButtonWords] = useState('Got it');
  const [previewApp, setPreviewApp] = useState<PreviewApp>('new');

  const agree = agreeWords.trim();
  const decline = declineWords.trim();
  const words = buttonWords.trim();
  const isConsent = taskType === 'consent';
  const isReadThis = taskType === 'read_this';
  const isForm = taskType === 'fill_form';
  const readThisButton = isReadThis && released;

  // Only these have a button on the new app and none on an older one. An older app never
  // gets a resume banner at all, and Reach a level is a plain banner on both.
  const hasAppSwitch = isConsent || readThisButton || isForm;
  const older = hasAppSwitch && previewApp === 'older';

  // Why Send is off, in the order the form asks for things.
  const missing = isConsent
    ? !agree
      ? 'Give the Agree button its words.'
      : allowDecline && !decline
      ? 'Give the Decline button its words, or do not let them decline.'
      : allowDecline && agree.toLowerCase() === decline.toLowerCase()
      ? 'The Agree and Decline buttons need different words.'
      : agree.includes('{{') || (allowDecline && decline.includes('{{'))
      ? DETAIL_IN_WORDS
      : null
    : readThisButton && !words
    ? 'Give the button its words.'
    : readThisButton && words.includes('{{')
    ? DETAIL_IN_WORDS
    : null;

  const sendBody = isConsent
    ? { consent: { agreeWords: agree, allowDecline, ...(allowDecline ? { declineWords: decline } : {}) } }
    : readThisButton
    ? { readThis: { buttonWords: words } }
    : {};

  const primary = older ? null : isConsent ? agree || 'Agree' : readThisButton ? words || 'Got it' : isReadThis ? null : ctaLabel;

  return {
    agreeWords,
    setAgreeWords,
    declineWords,
    setDeclineWords,
    allowDecline,
    setAllowDecline,
    buttonWords,
    setButtonWords,
    previewApp,
    setPreviewApp,
    hasAppSwitch,
    readThisButton,
    missing,
    sendBody,
    /** "Done when …", where the words are the sender's. */
    doneWhen: readThisButton ? `they press ${words || 'the button'}` : null,
    /** What the preview draws, for the app it is showing. */
    primary,
    secondary: !older && isConsent && allowDecline ? decline || 'Decline' : null,
    /** An answer stays where it is; only a button that opens a screen has an arrow. */
    arrow: !isConsent && !isReadThis,
    /** The server's first line on the card, for the app the preview is showing. */
    firstLine:
      older && (isConsent || (isForm && formBannerOnly))
        ? UPDATE_TO_ANSWER_LINE
        : older && isForm
        ? formLine
        : null,
  };
}

export type TaskButtons = ReturnType<typeof useTaskButtons>;
